import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, ChevronLeft, ChevronRight } from 'lucide-react'; 
import { newsService, advertiseService } from '../../services/contentService';
import menuService from '../../services/menuService';
import { getImageUrl, generateSlug } from '../../utils/helpers';

// ─────────────────────────────────────────────────────────────
// NÂNG CẤP: Dùng React.memo để tránh re-render thẻ <picture> liên tục
// ─────────────────────────────────────────────────────────────
const ResponsiveBannerImage = React.memo(({ banner, alt, pictureClassName, imgClassName, onError, isPriority, onLoad }) => {
    const defaultImg = getImageUrl(banner.advertiseImageUrl); 
    const desktopImg = banner.imageDesktop ? getImageUrl(banner.imageDesktop) : defaultImg;
    const mobileImg  = banner.imageMobile ? getImageUrl(banner.imageMobile) : defaultImg; 

    return (
        <picture className={`${pictureClassName} bg-gray-100`}>
            <source media="(min-width: 1920px)" srcSet={desktopImg} />
            <source media="(min-width: 768px)" srcSet={defaultImg} />   
            <img 
                src={mobileImg} 
                alt={alt || 'Banner'} 
                className={imgClassName} 
                width="1920"  
                height="360"  
                onLoad={onLoad} 
                onError={onError}
                loading={isPriority ? "eager" : "lazy"} 
                fetchpriority={isPriority ? "high" : "auto"}
                decoding={isPriority ? "sync" : "async"}
            />
        </picture>
    );
});

const HeroSection = () => {
    const [newsList, setNewsList] = useState([]);
    const [mainBanners, setMainBanners] = useState([]);
    const [subBanners, setSubBanners] = useState([]);
    const [menus, setMenus] = useState([]);
    
    const [loadingMain, setLoadingMain] = useState(true);
    const [loadingNews, setLoadingNews] = useState(true);

    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentSubSlide, setCurrentSubSlide] = useState(0);

    const [loadedMain, setLoadedMain] = useState({});
    const [pendingMainSlide, setPendingMainSlide] = useState(null);

    const [loadedSub, setLoadedSub] = useState({});
    const [pendingSubSlide, setPendingSubSlide] = useState(null);

    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);

    // ─────────────────────────────────────────────────────────────
    // NÂNG CẤP: Xử lý isMounted để chống Memory Leak
    // ─────────────────────────────────────────────────────────────
    useEffect(() => {
        let isMounted = true;

        const fetchMainContent = async () => {
            try {
                const [bannerData, menuData] = await Promise.all([
                    advertiseService.getPublicAdvertises().catch(() => []),
                    menuService.getPublicMenus().catch(() => []) 
                ]);
                
                if (!isMounted) return;

                const allBanners = Array.isArray(bannerData) ? bannerData : (bannerData?.data || []);
                setMainBanners(allBanners.filter(b => b.advertisePosition === 'MAIN_SLIDER' || !b.advertisePosition));
                setSubBanners(allBanners.filter(b => b.advertisePosition === 'SUB_BANNER'));
                
                const allMenus = Array.isArray(menuData) ? menuData : (menuData?.data || []);
                setMenus(allMenus);
            } catch (error) { 
                console.error(error); 
            } finally { 
                if (isMounted) setLoadingMain(false); 
            }
        };

        const fetchNewsContent = async () => {
            try {
                const newsData = await newsService.getPublicNews().catch(() => []);
                if (!isMounted) return;

                const allNews = Array.isArray(newsData) ? newsData : (newsData?.data || []);
                setNewsList(allNews);
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) setLoadingNews(false);
            }
        };

        fetchMainContent();
        fetchNewsContent();

        return () => { isMounted = false; };
    }, []);

    // ─────────────────────────────────────────────────────────────
    // NÂNG CẤP: Sắp xếp (Sort) Menu 1 lần duy nhất thay vì sort lúc Render
    // ─────────────────────────────────────────────────────────────
    const categoryMenus = useMemo(() => {
        const sortMenusRecursive = (menuList) => {
            if (!menuList || menuList.length === 0) return [];
            return [...menuList]
                .sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0))
                .map(m => ({
                    ...m,
                    subMenus: sortMenusRecursive(m.subMenus) // Đệ quy sort cả menu con
                }));
        };
        const rawCategoryMenus = menus.filter(m => m.menuPosition === 'CATEGORY');
        return sortMenusRecursive(rawCategoryMenus);
    }, [menus]);

    const handleMainLoad = useCallback((idx) => {
        setLoadedMain(prev => ({ ...prev, [idx]: true }));
    }, []);

    useEffect(() => {
        if (pendingMainSlide !== null && loadedMain[pendingMainSlide]) {
            setCurrentSlide(pendingMainSlide);
            setPendingMainSlide(null); 
        }
    }, [loadedMain, pendingMainSlide]);

    const nextSlide = useCallback((e) => {
        if (e) e.stopPropagation(); 
        setPendingMainSlide(prev => {
            const baseSlide = prev !== null ? prev : currentSlide;
            return (baseSlide + 1) % mainBanners.length;
        });
    }, [currentSlide, mainBanners.length]);

    const prevSlide = useCallback((e) => {
        if (e) e.stopPropagation();
        setPendingMainSlide(prev => {
            const baseSlide = prev !== null ? prev : currentSlide;
            return baseSlide === 0 ? mainBanners.length - 1 : baseSlide - 1;
        });
    }, [currentSlide, mainBanners.length]);

    // Timer cho Main Banner
    useEffect(() => {
        if (mainBanners.length <= 1) return; 
        const timer = setInterval(() => {
            nextSlide();
        }, 5000); 
        return () => clearInterval(timer);
    }, [mainBanners.length, nextSlide]); 

    const handleSubLoad = useCallback((idx) => {
        setLoadedSub(prev => ({ ...prev, [idx]: true }));
    }, []);

    useEffect(() => {
        if (pendingSubSlide !== null) {
            const nextSlot1 = pendingSubSlide;
            const nextSlot2 = (pendingSubSlide + 1) % subBanners.length;
            
            if (loadedSub[nextSlot1] && loadedSub[nextSlot2]) {
                setCurrentSubSlide(pendingSubSlide);
                setPendingSubSlide(null);
            }
        }
    }, [loadedSub, pendingSubSlide, subBanners.length]);

    // Timer cho Sub Banner
    useEffect(() => {
        if (subBanners.length <= 1) return;
        const timer = setInterval(() => {
            setPendingSubSlide((currentSubSlide + 1) % subBanners.length);
        }, 10000); 
        return () => clearInterval(timer);
    }, [subBanners.length, currentSubSlide]);

    const handleBannerClick = (url) => {
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleTouchStart = (e) => {
        setTouchStartX(e.targetTouches[0].clientX);
        setTouchEndX(null);
    };

    const handleTouchMove = (e) => setTouchEndX(e.targetTouches[0].clientX);

    const handleTouchEnd = () => {
        if (touchStartX === null || touchEndX === null) return;
        const distance = touchStartX - touchEndX;
        const minSwipeDistance = 50; 
        
        if (distance > minSwipeDistance) {
            nextSlide(); 
        } else if (distance < -minSwipeDistance) {
            prevSlide(); 
        }

        // NÂNG CẤP: Reset lại toạ độ sau khi swipe xong
        setTouchStartX(null);
        setTouchEndX(null);
    };

    return (
        <div className="container mx-auto px-4 xl:px-8 py-4">
            <div className="flex flex-col lg:flex-row items-start gap-3 relative">
                
                {/* Khu vực Category Menu */}
                <div className="hidden lg:block w-[260px] xl:w-[280px] flex-shrink-0 z-50 relative">
                    <div className="bg-gradient-to-r from-orange-100 to-orange-50 w-full shadow-md border border-orange-300/90 rounded-xl py-2 min-h-[360px]">
                        {loadingMain ? (
                            <div className="py-2 px-4 flex flex-col gap-4">
                                {[...Array(9)].map((_, i) => (
                                    <div key={i} className="flex items-center gap-3 opacity-60">
                                        <div className="w-5 h-5 bg-orange-200/80 rounded-full animate-pulse"></div>
                                        <div className="h-4 bg-orange-200/80 rounded w-3/4 animate-pulse"></div>
                                    </div>
                                ))}
                            </div>
                        ) : categoryMenus.length > 0 && (
                            <ul className="flex flex-col relative py-2">
                                {categoryMenus.map((cat) => (
                                    <li key={cat.menuId} className="group/item static border-b border-orange-100/40 last:border-0">
                                        <Link to={`/danh-muc/${generateSlug(cat.menuName)}-id${cat.menuId}`} className="flex items-stretch text-[13px] font-bold transition-all duration-200 relative z-10 text-gray-800 group-hover/item:text-white group-hover/item:bg-tnc-orange rounded-xl mx-2">
                                            <div className="flex items-center justify-center pl-4 pr-3 py-2.5 shrink-0">
                                                {cat.menuIcon ? (
                                                    <img src={getImageUrl(cat.menuIcon)} alt="" className="w-5 h-5 object-contain transition-transform duration-300 group-hover/item:scale-110 group-hover/item:brightness-0 group-hover/item:invert" />
                                                ) : (<div className="w-5 h-5"></div>)}
                                            </div>
                                            <div className="flex-1 flex items-center justify-between pr-4 pl-1 py-2.5 transition-all duration-300">
                                                <span className="whitespace-normal break-words leading-snug transition-transform duration-300 origin-left group-hover/item:translate-x-1">{cat.menuName}</span>
                                                {cat.subMenus && cat.subMenus.length > 0 && <ChevronRight size={16} className="shrink-0 ml-2 transition-colors text-gray-400 group-hover/item:text-white" />}
                                            </div>
                                        </Link>

                                        {cat.subMenus && cat.subMenus.length > 0 && (
                                            <div className="absolute top-0 left-full pl-1 hidden group-hover/item:block z-50 min-h-full">
                                                <div className="absolute top-0 -left-4 w-6 h-full bg-transparent z-[-1]"></div>
                                                <div className="w-max min-w-[250px] max-w-[850px] min-h-[360px] max-h-[80vh] pr-8 bg-gradient-to-br from-orange-50 to-white shadow-xl border border-orange-200/60 rounded-xl p-6 overflow-y-auto custom-scrollbar">
                                                    <div className="flex flex-wrap gap-x-12 gap-y-8">
                                                        {cat.subMenus.map(sub => (
                                                            <div key={sub.menuId} className="flex flex-col min-w-[160px] max-w-[280px]">
                                                                <Link to={`/danh-muc/${generateSlug(sub.menuName)}-id${sub.menuId}`} className="flex items-center gap-2.5 font-bold text-tnc-orange text-sm mb-3 pb-2 border-b border-orange-200/60 leading-snug">
                                                                    {sub.menuIcon && <img src={getImageUrl(sub.menuIcon)} alt="" className="w-7 h-7 object-contain shrink-0" />}
                                                                    <span className="whitespace-normal break-words uppercase">{sub.menuName}</span>
                                                                </Link>
                                                                {sub.subMenus && sub.subMenus.length > 0 && (
                                                                    <ul className="flex flex-col space-y-2">
                                                                        {sub.subMenus.map(sub3 => (
                                                                            <li key={sub3.menuId} className="relative group/sub3">
                                                                                <Link to={`/danh-muc/${generateSlug(sub3.menuName)}-id${sub3.menuId}`} className="group/link3 flex items-center justify-between text-[13px] font-bold text-gray-800 transition-all duration-200 py-1.5 w-max border-b-2 border-transparent hover:border-tnc-orange hover:text-tnc-orange">
                                                                                    <div className="flex items-center">
                                                                                        {sub3.menuIcon ? (
                                                                                            <img src={getImageUrl(sub3.menuIcon)} alt="" className="w-7 h-7 object-contain shrink-0 mr-2.5" />
                                                                                        ) : (
                                                                                            <span className="text-gray-300 group-hover/link3:text-tnc-orange mr-2 font-black text-[10px] shrink-0">•</span>
                                                                                        )}
                                                                                        <span className="whitespace-normal break-words leading-snug">{sub3.menuName}</span>
                                                                                    </div>
                                                                                    {sub3.subMenus && sub3.subMenus.length > 0 && <ChevronRight size={14} className="ml-2 shrink-0 text-gray-400 group-hover/link3:text-tnc-orange" />}
                                                                                </Link>
                                                                                
                                                                                {sub3.subMenus && sub3.subMenus.length > 0 && (
                                                                                    <div className="absolute top-0 left-full pl-4 hidden group-hover/sub3:block z-50">
                                                                                        <div className="absolute top-0 -left-4 w-6 h-full bg-transparent z-[-1]"></div>
                                                                                        <div className="w-max min-w-[200px] bg-gradient-to-br from-orange-50 to-white shadow-lg border border-orange-200/60 rounded-lg p-4">
                                                                                            <ul className="flex flex-col space-y-2">
                                                                                                {sub3.subMenus.map(sub4 => (
                                                                                                    <li key={sub4.menuId}>
                                                                                                        <Link to={`/danh-muc/${generateSlug(sub4.menuName)}-id${sub4.menuId}`} className="group/link4 flex items-center text-[13px] font-bold text-gray-800 transition-all duration-200 py-1.5 w-max border-b-2 border-transparent hover:border-tnc-orange hover:text-tnc-orange">
                                                                                                            {sub4.menuIcon ? (
                                                                                                                <img src={getImageUrl(sub4.menuIcon)} alt="" className="w-4 h-4 object-contain shrink-0 mr-2.5" />
                                                                                                            ) : (
                                                                                                                <span className="text-gray-300 group-hover/link4:text-tnc-orange mr-2 font-black text-[10px] shrink-0">•</span>
                                                                                                            )}
                                                                                                            <span className="whitespace-normal break-words leading-snug">{sub4.menuName}</span>
                                                                                                        </Link>
                                                                                                    </li>
                                                                                                ))}
                                                                                            </ul>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="flex-1 flex flex-col gap-3 min-w-0 z-10 w-full">
                    
                    {/* KHU VỰC MAIN BANNER */}
                    <div className="w-full h-[220px] md:h-[280px] lg:h-[360px] overflow-hidden rounded-xl shadow-sm group relative bg-white border border-orange-200/60 flex-shrink-0">
                        {loadingMain ? (
                            <div className="w-full h-full bg-gray-200 animate-pulse"></div>
                        ) : mainBanners.length > 0 ? (
                            <>
                                {pendingMainSlide !== null && (
                                    <div className="absolute top-4 right-4 z-30 bg-black/40 backdrop-blur-md rounded-full p-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}

                                <div 
                                    className="w-full h-full relative bg-gray-50 touch-pan-y"
                                    onClick={(e) => {
                                        if (touchStartX !== null && touchEndX !== null && Math.abs(touchStartX - touchEndX) > 20) {
                                            e.preventDefault(); return;
                                        }
                                        handleBannerClick(mainBanners[currentSlide].advertiseUrl);
                                    }}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                >
                                    {mainBanners.map((slide, idx) => (
                                        <ResponsiveBannerImage 
                                            key={slide.advertiseId || idx}
                                            banner={slide} 
                                            alt={slide.advertiseTitle}
                                            isPriority={idx === 0 || idx === currentSlide || idx === pendingMainSlide}
                                            onLoad={() => handleMainLoad(idx)}
                                            onError={(e) => { 
                                                e.target.onerror = null;
                                                e.target.src = getImageUrl(null); 
                                                handleMainLoad(idx); 
                                            }}
                                            pictureClassName={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${currentSlide === idx ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                                            imgClassName="w-full h-full object-contain" 
                                        />
                                    ))}
                                </div>

                                {mainBanners.length > 1 && (
                                    <>
                                        <button onClick={prevSlide} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-tnc-orange text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 shadow-md backdrop-blur-sm">
                                            <ChevronLeft size={24} />
                                        </button>
                                        <button onClick={nextSlide} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-tnc-orange text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 shadow-md backdrop-blur-sm">
                                            <ChevronRight size={24} />
                                        </button>
                                        
                                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                                            {mainBanners.map((_, idx) => (
                                                <button 
                                                    key={idx} 
                                                    onClick={(e) => { e.stopPropagation(); setPendingMainSlide(idx); }}
                                                    className={`h-2 rounded-full transition-all duration-300 shadow-sm ${currentSlide === idx ? 'bg-tnc-orange w-6' : 'bg-white/70 hover:bg-white w-2'}`}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl relative">
                                <img src={getImageUrl(null, "Banner Chính")} alt="Banner Default" className="absolute inset-0 w-full h-full object-cover" />
                            </div>
                        )}
                    </div>

                    {/* KHU VỰC SUB BANNER */}
                    {loadingMain ? (
                        <div className="grid grid-cols-2 gap-3 w-full h-[100px] md:h-[130px] lg:h-[200px] flex-shrink-0">
                            <div className="w-full h-full bg-gray-200 animate-pulse rounded-xl"></div>
                            <div className="w-full h-full bg-gray-200 animate-pulse rounded-xl"></div>
                        </div>
                    ) : subBanners.length > 0 && (
                        <div className="grid grid-cols-2 gap-3 w-full h-[100px] md:h-[130px] lg:h-[200px] flex-shrink-0">
                            
                            {/* Cột 1 (Sub Banner 1) */}
                            <div className="relative w-full h-full rounded-xl overflow-hidden shadow-sm border border-orange-200/60 bg-white group">
                                {subBanners.map((banner, idx) => (
                                    <div 
                                        key={`sub1-${banner.advertiseId || idx}`}
                                        className={`absolute inset-0 w-full h-full transition-opacity duration-1000 cursor-pointer ${currentSubSlide === idx ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                                        onClick={() => currentSubSlide === idx && handleBannerClick(banner.advertiseUrl)}
                                    >
                                        <ResponsiveBannerImage 
                                            banner={banner}
                                            alt={banner.advertiseTitle || `Sub Banner 1`}
                                            isPriority={idx === 0 || idx === 1 || idx === currentSubSlide || idx === pendingSubSlide}
                                            onLoad={() => handleSubLoad(idx)}
                                            onError={(e) => { 
                                                e.target.onerror = null; 
                                                e.target.src = getImageUrl(null); 
                                                handleSubLoad(idx); 
                                            }}
                                            pictureClassName="w-full h-full block rounded-lg overflow-hidden"
                                            imgClassName="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Cột 2 (Sub Banner 2) */}
                            <div className="relative w-full h-full rounded-xl overflow-hidden shadow-sm border border-orange-200/60 bg-white group">
                                {subBanners.length === 1 ? (
                                    <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-400 text-sm">
                                        Quảng cáo
                                    </div>
                                ) : (
                                    subBanners.map((banner, idx) => {
                                        const isCurrentSlot2 = idx === (currentSubSlide + 1) % subBanners.length;
                                        const isPendingSlot2 = pendingSubSlide !== null && idx === (pendingSubSlide + 1) % subBanners.length;
                                        
                                        return (
                                            <div 
                                                key={`sub2-${banner.advertiseId || idx}`}
                                                className={`absolute inset-0 w-full h-full transition-opacity duration-1000 cursor-pointer ${isCurrentSlot2 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                                                onClick={() => isCurrentSlot2 && handleBannerClick(banner.advertiseUrl)}
                                            >
                                                <ResponsiveBannerImage 
                                                    banner={banner}
                                                    alt={banner.advertiseTitle || `Sub Banner 2`}
                                                    isPriority={idx === 0 || idx === 1 || isCurrentSlot2 || isPendingSlot2}
                                                    onLoad={() => handleSubLoad(idx)}
                                                    onError={(e) => { 
                                                        e.target.onerror = null; 
                                                        e.target.src = getImageUrl(null); 
                                                        handleSubLoad(idx); 
                                                    }}
                                                    pictureClassName="w-full h-full block rounded-lg overflow-hidden"
                                                    imgClassName="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                                />
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                        </div>
                    )}
                </div>

                {/* KHU VỰC BÊN PHẢI (Build PC & Tin tức) */}
                <div className="w-full lg:w-[240px] xl:w-[260px] flex-shrink-0 flex flex-col gap-3 z-10">
                    <Link to="/build-pc" className="bg-gradient-to-r from-orange-100 to-white border border-orange-200/60 rounded-xl shadow-sm flex items-center p-3 gap-3 cursor-pointer hover:border-tnc-orange hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
                        <div className="bg-white p-2 rounded-full text-tnc-orange group-hover:bg-tnc-orange group-hover:text-white transition-colors shadow-sm">
                            <Wrench size={22}/>
                        </div>
                        <div className="font-extrabold text-sm text-gray-800 uppercase tracking-tight group-hover:text-tnc-orange transition-colors">
                            Xây dựng<br/>Cấu hình PC
                        </div>
                    </Link>

                    <div className="bg-white border border-orange-200/60 rounded-xl shadow-sm overflow-hidden flex-1 flex flex-col h-auto">
                        <Link to="/tin-tuc" state={{ categoryId: 'featured' }} className="bg-tnc-orange text-white font-bold py-2.5 px-4 uppercase text-sm block hover:bg-tnc-orange-hover transition-colors flex items-center justify-between group">
                            Tin tức nổi bật
                            <ChevronRight size={16} className="opacity-70 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />
                        </Link>
                        
                        <div className="bg-gradient-to-r from-orange-100 to-white p-3 flex flex-col gap-3 flex-1 overflow-y-auto border-t border-orange-200/60 min-h-[350px]"> 
                            {loadingNews ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i} className="flex gap-3 items-start border-b border-orange-100 pb-3 last:border-b-0 last:pb-0 animate-pulse">
                                        <div className="w-24 h-[60px] lg:w-20 lg:h-[54px] xl:w-24 xl:h-[64px] bg-orange-200/50 rounded flex-shrink-0"></div>
                                        <div className="flex-1 flex flex-col gap-2 pt-1">
                                            <div className="h-3 bg-orange-200/60 rounded w-full"></div>
                                            <div className="h-3 bg-orange-200/60 rounded w-4/5"></div>
                                        </div>
                                    </div>
                                ))
                            ) : newsList.filter(item => item.isFeatured).length > 0 ? ( 
                                newsList.filter(item => item.isFeatured).slice(0, 5).map((item) => (
                                    <div key={item.newsId} className="flex gap-3 items-start border-b border-orange-100 pb-3 last:border-b-0 last:pb-0">
                                        <div className="w-24 h-[60px] lg:w-20 lg:h-[54px] xl:w-24 xl:h-[64px] bg-gray-100 flex-shrink-0 rounded overflow-hidden shadow-sm border border-orange-100">
                                            <img 
                                                src={getImageUrl(item.newsImageUrl)} 
                                                alt={item.newsTitle} 
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                                onError={(e) => { 
                                                    e.target.onerror = null; 
                                                    e.target.src = getImageUrl(null, "Tin Tức"); 
                                                }}
                                            />
                                        </div>
                                        
                                        <div className="flex flex-col justify-start w-full">
                                            <Link 
                                                to={`/tin-tuc/${generateSlug(item.newsTitle)}-id${item.newsId}`} 
                                                className="text-[13px] lg:text-[12px] xl:text-[13px] font-bold text-gray-700 hover:text-tnc-orange line-clamp-3 leading-[1.6] transition-colors"
                                            >
                                                {item.newsTitle}
                                            </Link>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-xs text-center text-gray-500 py-4">Chưa có tin tức nổi bật.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeroSection;